<template>
  <page>
    <review-provider
      v-model="review"
      :review-query="GetReviewForReviewPage"
      subscribe-to-upload-updates
      :load-uploads="uploadsRequested"
      :loading.sync="loading"
      :uploads-loading.sync="uploadsLoading"
      @update:error="redirectToReviewsPage"
    />
    <template #header-bar>
      <review-page-header
        class="px-9"
        :review="review"
        :loading="loading"
        :disable-download="!hasReferences"
        @upload="openUploadModal()"
        @download-references="showDownloadDialog = true"
        @export-review="showExportDialog = true"
        @show-prisma-stats="showPrismaDialog = true"
      />
    </template>
    <template #content>
      <page-tabs
        v-if="hasReferences"
        v-model="activeTabIndex"
        use-tab-routes
        :tabs="tabs"
      >
        <page-tab-panel class="page-tab pa-6">
          <div>
            <dashboard v-if="!loading" :review="review" />
            <dashboard-skeleton-loader v-else />
          </div>
        </page-tab-panel>
        <page-tab-panel>
          <references-tab-controller
            v-if="!loading"
            :review="review"
          />
        </page-tab-panel>
        <page-tab-panel>
          <duplicates-tab v-if="!loading" :review="review" />
        </page-tab-panel>
        <page-tab-panel class="page-tab">
          <v-col class="pa-6 ma-0">
            <div class="d-flex flex-row-reverse mb-4">
              <action-button color="primary" @click="openUploadModal">
                <v-icon left class="material-icons-outlined">
                  cloud_upload
                </v-icon>
                Upload References
              </action-button>
            </div>
            <div class="fill-height fill-width">
              <uploads-page-skeleton-loader
                v-if="loading || uploadsLoading"
              />
              <div
                v-else-if="!uploadsData || uploadsData.length < 1"
                class="d-flex justify-center align-center fill-height"
              >
                <upload-references-page-placeholder />
              </div>
              <upload-group-list
                v-else
                :review-id="review.id"
                :upload-groups="uploadsData"
                :can-delete="
                  Boolean(review && review.currentUserCanEditReview())
                "
              />
            </div>
          </v-col>
        </page-tab-panel>
        <page-tab-panel
          v-if="review.livingReview"
          class="page-tab pt-6"
        >
          <div>
            <revision-card-list :review="review" />
          </div>
        </page-tab-panel>
      </page-tabs>
      <v-row
        v-else-if="!loading"
        no-gutters
        justify="center"
        align="center"
      >
        <upload-references-page-placeholder
          v-if="!uploadsAreInProgress"
        />
        <uploads-in-progress-page-placeholder v-else />
      </v-row>
      <template v-if="review">
        <upload-references-modal
          :review="review"
          @resolve-duplicates="redirectToDuplicatesPage"
        />
        <prisma-stats-dialog
          v-model="showPrismaDialog"
          :review-id="review.id"
        />
        <download-references-controller
          v-model="showDownloadDialog"
          :review="review"
        />
        <export-review-dialog
          v-model="showExportDialog"
          :fields="review.dataExtractionFields"
          @export="exportReview"
        />
      </template>
    </template>
  </page>
</template>

<script>
import Page from '@/components/Common/Page';
import PageTabs from '@/components/Common/PageTabs';
import PageTabPanel from '@/components/Common/PageTabPanel';
import ActionButton from '@/components/Common/ActionButton';
import ReviewPageHeader from '@/components/ReviewPage/ReviewPageHeader';
import ReviewProvider from '@/components/Providers/ReviewProvider';
import UploadReferencesPagePlaceholder from '@/components/UploadReferences/UploadReferencesPagePlaceholder';
import UploadsInProgressPagePlaceholder from '@/components/UploadReferences/UploadsInProgressPagePlaceholder';
import DownloadReferencesController from '@/components/ReviewPage/DownloadReferencesController/DownloadReferencesController';
import ExportReviewDialog from '@/components/ReviewPage/ExportReviewDialog';
import UploadReferencesModal from '@/components/UploadReferences/UploadReferencesModal';
import PrismaStatsDialog from '@/components/ReviewPage/PrismaStatsController/PrismaStatsDialog';
import DashboardSkeletonLoader from '@/components/DashboardTab/DashboardSkeletonLoader';
import Dashboard from '@/components/DashboardTab/Dashboard';
import ReferencesTabController from '@/components/ReferencesTab/ReferencesTabController';
import DuplicatesTab from '@/components/DuplicatesTab/DuplicatesTab';
import UploadGroupList from '@/components/UploadsPage/UploadGroupList';
import UploadsPageSkeletonLoader from '@/components/UploadsPage/UploadsPageSkeletonLoader';
import RevisionCardList from '@/components/RevisionsTab/MVP/RevisionCardList';
import { mapActions, mapGetters } from 'vuex';
import { GetReviewForReviewPage } from '@/graphql';

export default {
  name: 'ReviewPageV2',
  components: {
    Page,
    PageTabs,
    PageTabPanel,
    ActionButton,
    ReviewPageHeader,
    ReviewProvider,
    UploadReferencesPagePlaceholder,
    UploadsInProgressPagePlaceholder,
    UploadReferencesModal,
    DownloadReferencesController,
    ExportReviewDialog,
    PrismaStatsDialog,
    DashboardSkeletonLoader,
    Dashboard,
    ReferencesTabController,
    DuplicatesTab,
    UploadGroupList,
    UploadsPageSkeletonLoader,
    RevisionCardList,
  },
  data: () => ({
    review: null,
    loading: true,
    showDownloadDialog: false,
    showExportDialog: false,
    showPrismaDialog: false,
    activeTabIndex: 0,
    uploadsRequested: false,
    uploadsLoading: false,
    GetReviewForReviewPage,
  }),
  computed: {
    ...mapGetters({
      uploadsAreInProgress: 'uploadReferences/uploadsAreInProgress',
    }),
    hasReferences() {
      return this.review?.uniqueReferencesCount > 0;
    },
    uploadsData() {
      // UploadGroupList expects UploadGroupDto groups of ReferenceFile
      // models, not the raw flat uploads array.
      if (!this.review || !this.uploadsRequested) return null;
      return this.review.uploadGroups();
    },
    tabs() {
      const tabs = [
        'Dashboard',
        'References',
        'Duplicates',
        'Import',
      ];
      if (this.review?.livingReview) {
        tabs.push('Revisions');
      }
      return tabs;
    },
  },
  watch: {
    activeTabIndex() {
      this.maybeLoadUploads();
    },
    // With use-tab-routes the displayed tab comes from the route param and
    // no input event fires on deep links or back/forward navigation; the
    // review also arrives asynchronously. Cover all paths to the Import tab.
    '$route.params.tab': function () {
      this.maybeLoadUploads();
    },
    review() {
      this.maybeLoadUploads();
    },
  },
  methods: {
    ...mapActions({
      openUploadModal: 'uploadReferences/openModal',
      exportReviewAndGetUrl: 'review/exportReview',
    }),
    redirectToReviewsPage() {
      if (this.$route.path !== '/reviews') {
        this.$router.push('/reviews');
      }
    },
    redirectToDuplicatesPage() {
      this.$router.push(`/review/${this.review.id}/duplicates`);
    },
    redirectToScreeningWorkflow() {
      this.$router.push(`/review/${this.review.id}/screening`);
    },
    async exportReview({ options, groupByField }) {
      const url = await this.exportReviewAndGetUrl({
        review: this.review,
        field: groupByField,
        options,
      });
      window.location.href = url;
      this.showExportDialog = false;
    },
    maybeLoadUploads() {
      if (this.uploadsRequested) return;
      // Mirror PageTabs' use-tab-routes behaviour: the route param names the
      // tab; the v-model index only tracks explicit tab clicks.
      const routeTab = this.$route.params.tab;
      const importTabShowing = routeTab
        ? routeTab === 'import'
        : this.tabs[this.activeTabIndex] === 'Import';
      if (importTabShowing) {
        // The provider owns the uploads query and keeps them fresh on
        // refetches; it merges them into `review`.
        this.uploadsRequested = true;
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.page-tab {
  display: flex;
  justify-content: center;
}
</style>
